/**
 * Profile language
 *
 * Applies the UI language stored on the user profile (`user.language`) to i18n.
 * Used after a successful login (Public-Chat flow) and on app start, when an
 * existing session is restored from the user store.
 */

import { useEffect, useRef } from 'react';
import i18n from 'i18n';
import { getUserInfo } from 'API/user';
import useUserStore from 'stores/user-store';

const SUPPORTED_LANGUAGES = ['de', 'en'];

/** Normalizes 'de-DE' -> 'de' and returns undefined for unsupported values. */
function normalizeLanguage(language: string | undefined | null): string | undefined {
  if (!language || typeof language !== 'string') return undefined;
  const base = language.toLowerCase().split('-')[0];
  return SUPPORTED_LANGUAGES.includes(base) ? base : undefined;
}

/**
 * Switches i18n to the profile language — but only if it is set, supported and
 * actually different from the current language (no needless re-render loop).
 */
export function applyProfileLanguage(language: string | undefined | null): void {
  const next = normalizeLanguage(language);
  if (!next) return;
  const current = normalizeLanguage(i18n.language);
  if (current === next) return;
  void i18n.changeLanguage(next);
}

/**
 * Keeps the UI language in sync with the language stored on the logged-in
 * user's profile. Mount once at app root — it covers the session restore on
 * app start as well as later profile updates.
 */
export function useProfileLanguage(): void {
  const profileLanguage = useUserStore((state) => state.details?.language);
  const hasToken = useUserStore((state) => !!state.token);
  const refreshed = useRef(false);

  useEffect(() => {
    applyProfileLanguage(profileLanguage);
  }, [profileLanguage]);

  // Session restore: locally stored details may predate the language field
  // (older logins only persisted id/email). Fetch the profile once and update
  // the store so the language above kicks in.
  useEffect(() => {
    if (!hasToken || profileLanguage || refreshed.current) return;
    refreshed.current = true;
    void (async () => {
      const userInfo = await getUserInfo();
      if (userInfo && !userInfo.error && userInfo.language) {
        const { details, setDetails } = useUserStore.getState();
        setDetails({ ...details, ...userInfo });
        applyProfileLanguage(userInfo.language);
      }
    })();
  }, [hasToken, profileLanguage]);
}

export default useProfileLanguage;
